/**
 * Holostaff SDK v1 — PRD §5.
 *
 * Public surface (six methods + three lifecycle helpers):
 *   identify(userId, traits)
 *   group(accountId, traits)
 *   page(name, properties)
 *   track(event, properties)
 *   signal(name, value)
 *   reset()
 *   consent(allowed)
 *   ready(callback)
 *
 * Design priorities:
 *   - Opt-in, zero-config: widget works without any SDK calls.
 *   - Batching: calls accumulate in a buffer and flush on a 200ms tick
 *     or immediately on window.beforeunload.
 *   - Privacy: PII hashing by default (email/name → SHA-256).
 *   - Consent gating: respects navigator.doNotTrack unless configured
 *     otherwise; customers can call Holostaff.consent(false) to suspend.
 *   - Pre-load queue: replaces any Array-shaped `window.Holostaff` from
 *     the Segment-style loader snippet and drains its queued calls.
 *   - Fail-soft: SDK errors never throw to the host application.
 */

import {
  collectSessionContext,
  currentPage,
  currentViewport,
  type HolostaffContext,
} from './context.js'
import { hashPiiFields } from './hashing.js'

export type PiiMode = 'hashed' | 'raw'

export interface SdkOptions {
  /** Staff / copilot identifier for the current deployment. */
  staffId: string
  /** Visitor ID already minted by the widget. */
  visitorId: string
  /** Base URL of the Holostaff vision server (hosts the SDK ingest endpoint). */
  ingestBaseUrl: string
  /** Library name + version reported in context.library. */
  library?: { name: string; version: string }
  /** PII handling — `'hashed'` (default) or `'raw'` (opt-in for CRM integration use cases). */
  piiMode?: PiiMode
  /** When true, navigator.doNotTrack is ignored. Default: respect DNT. */
  respectDnt?: boolean
  /** Flush cadence in ms. Default 200. */
  flushIntervalMs?: number
  /** Max queue depth before events are dropped (oldest first). Default 500. */
  maxQueueDepth?: number
}

export type SdkMethod =
  | 'identify'
  | 'group'
  | 'page'
  | 'track'
  | 'signal'
  | 'reset'

interface QueuedEvent {
  method: SdkMethod
  at: string
  data: Record<string, unknown>
}

/**
 * Array-shaped pre-load stub installed by the Segment-style snippet.
 * The SDK drains `stub.q` on construction.
 */
type PreloadStub = {
  q?: Array<[string, IArguments | unknown[]]>
}

export class HolostaffSdk {
  readonly staffId: string
  readonly visitorId: string
  readonly sessionId: string
  readonly library: { name: string; version: string }

  private ingestUrl: string
  private piiMode: PiiMode
  private consentOk: boolean
  private context: HolostaffContext
  private queue: QueuedEvent[] = []
  private maxQueueDepth: number
  private flushIntervalMs: number
  private flushTimer: ReturnType<typeof setInterval> | null = null
  private readyCallbacks: Array<(sdk: HolostaffSdk) => void> = []
  private currentUserId: string | null = null
  private currentAccountId: string | null = null

  constructor(opts: SdkOptions) {
    this.staffId = opts.staffId
    this.visitorId = opts.visitorId
    this.library = opts.library ?? { name: 'holostaff-widget', version: '0.0.0' }
    this.piiMode = opts.piiMode ?? 'hashed'
    this.flushIntervalMs = opts.flushIntervalMs ?? 200
    this.maxQueueDepth = opts.maxQueueDepth ?? 500
    this.ingestUrl = opts.ingestBaseUrl.replace(/\/$/, '') + '/api/sdk/ingest'

    // Session id: one per widget load. Unrelated to the visitor cookie;
    // sessions restart on each page load / bundle reload.
    this.sessionId = `sess_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`

    // Auto-context — captured once; viewport re-emitted on resize.
    const { context } = collectSessionContext(this.library)
    this.context = context

    // Default consent: respect DNT unless caller opted out.
    const respectDnt = opts.respectDnt !== false
    const dnt = typeof navigator !== 'undefined' && (navigator as any).doNotTrack === '1'
    this.consentOk = !(respectDnt && dnt)

    this.installLifecycleListeners()
    this.startFlushTimer()
  }

  // -----------------------------------------------------------------------
  // Public API
  // -----------------------------------------------------------------------

  identify(userId: string, traits: Record<string, unknown> = {}): void {
    this.currentUserId = userId
    this.enqueuePii('identify', { userId, traits })
  }

  group(accountId: string, traits: Record<string, unknown> = {}): void {
    this.currentAccountId = accountId
    this.enqueuePii('group', { accountId, traits })
  }

  page(name?: string, properties: Record<string, unknown> = {}): void {
    const snap = currentPage() ?? {}
    this.context.page = { ...this.context.page, ...snap }
    this.enqueue('page', { name: name ?? '', properties, page: this.context.page })
  }

  track(event: string, properties: Record<string, unknown> = {}): void {
    this.enqueue('track', { event, properties })
  }

  signal(name: string, value: unknown): void {
    this.enqueue('signal', { name, value })
  }

  reset(): void {
    this.currentUserId = null
    this.currentAccountId = null
    // Enqueue a reset marker so the server can clear session user/account scope.
    this.enqueue('reset', {})
  }

  /** Toggle consent. When false, subsequent calls are no-ops until consent(true). */
  consent(allowed: boolean): void {
    this.consentOk = allowed
    if (!allowed) this.queue = []
  }

  /** Resolve when the SDK is fully initialised (always already, once constructed). */
  ready(callback: (sdk: HolostaffSdk) => void): void {
    this.readyCallbacks.push(callback)
    try {
      callback(this)
    } catch { /* swallow — fail-soft */ }
  }

  // -----------------------------------------------------------------------
  // Internal — enqueue / flush
  // -----------------------------------------------------------------------

  private enqueue(method: SdkMethod, data: Record<string, unknown>): void {
    if (!this.consentOk) return
    this.queue.push({ method, at: new Date().toISOString(), data })
    if (this.queue.length > this.maxQueueDepth) {
      // Drop oldest overflow — keeps memory bounded under misuse.
      this.queue.splice(0, this.queue.length - this.maxQueueDepth)
    }
  }

  /**
   * Enqueue with PII hashing. identify + group may carry email/name/phone
   * fields that we hash client-side unless piiMode === 'raw'.
   */
  private enqueuePii(method: SdkMethod, data: Record<string, unknown>): void {
    if (!this.consentOk) return
    const traits = (data.traits ?? {}) as Record<string, unknown>
    if (this.piiMode === 'raw') {
      this.enqueue(method, data)
      return
    }
    hashPiiFields(traits)
      .then((hashedTraits) => {
        this.enqueue(method, { ...data, traits: hashedTraits })
      })
      .catch(() => {
        // Hashing failed (no Web Crypto?) — drop PII fields defensively.
        const safe = { ...traits }
        for (const f of ['email', 'name', 'full_name', 'phone']) delete (safe as any)[f]
        this.enqueue(method, { ...data, traits: safe })
      })
  }

  private startFlushTimer(): void {
    if (this.flushTimer) return
    this.flushTimer = setInterval(() => this.flush(), this.flushIntervalMs)
    if (typeof (this.flushTimer as { unref?: () => void }).unref === 'function') {
      ;(this.flushTimer as { unref: () => void }).unref()
    }
  }

  private installLifecycleListeners(): void {
    if (typeof window === 'undefined') return
    // Keep viewport fresh.
    window.addEventListener('resize', () => {
      const vp = currentViewport()
      if (vp) this.context.viewport = vp
    })
    // Flush on page hide — best-effort delivery.
    const flushOnHide = () => {
      try { this.flush(true) } catch { /* no-op */ }
    }
    window.addEventListener('beforeunload', flushOnHide)
    window.addEventListener('pagehide', flushOnHide)
  }

  /**
   * Flush the queue to the ingest endpoint. Uses fetch by default; falls
   * back to sendBeacon on page hide for best-effort reliability.
   */
  private flush(urgent = false): void {
    if (this.queue.length === 0) return
    const batch = this.queue.splice(0, this.queue.length)
    const body = JSON.stringify({
      staffId: this.staffId,
      visitorId: this.visitorId,
      sessionId: this.sessionId,
      userId: this.currentUserId,
      accountId: this.currentAccountId,
      context: this.context,
      library: this.library,
      piiMode: this.piiMode,
      batch,
    })

    if (urgent && typeof navigator !== 'undefined' && navigator.sendBeacon) {
      try {
        navigator.sendBeacon(this.ingestUrl, new Blob([body], { type: 'application/json' }))
        return
      } catch {
        // fall through to fetch
      }
    }

    fetch(this.ingestUrl, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body,
      keepalive: urgent,
    }).catch(() => {
      // Fail-soft: drop the batch. SDK errors never propagate.
    })
  }

  // -----------------------------------------------------------------------
  // Test / developer surface
  // -----------------------------------------------------------------------

  /** Current context snapshot — exposed for dev inspection. */
  getContext(): HolostaffContext {
    return this.context
  }

  /** Flush synchronously. Primarily for tests. */
  _flushNow(): void {
    this.flush(false)
  }
}

// -------------------------------------------------------------------------
// Pre-load queue draining
// -------------------------------------------------------------------------

/**
 * Install the SDK as window.Holostaff, replacing any array-shaped stub
 * installed by the Segment-style snippet. Drains stub.q by invoking the
 * corresponding method on the real SDK instance.
 */
export function installGlobal(sdk: HolostaffSdk): void {
  if (typeof window === 'undefined') return
  const w = window as unknown as { Holostaff?: PreloadStub | HolostaffSdk }
  const existing = w.Holostaff as PreloadStub | HolostaffSdk | undefined

  // Expose the real SDK.
  w.Holostaff = sdk

  // If there was a preload stub with a queue, drain it.
  if (existing && typeof existing !== 'function' && 'q' in existing && Array.isArray((existing as PreloadStub).q)) {
    const queued = (existing as PreloadStub).q ?? []
    for (const [method, args] of queued) {
      const fn = (sdk as unknown as Record<string, (...a: unknown[]) => void>)[method]
      if (typeof fn === 'function') {
        try {
          fn.apply(sdk, Array.from(args as ArrayLike<unknown>))
        } catch { /* fail-soft */ }
      }
    }
  }
}
